import React, { useEffect, useRef } from "react";
import { MdOutlineInfo } from "react-icons/md";
import "./SharedStyles.scss";

interface IProps {
  message: string;
}

const hasOwnStyles = (html: string): boolean =>
  /<style[\s>]/i.test(html) ||
  /<link[^>]+stylesheet/i.test(html) ||
  /<!DOCTYPE/i.test(html);

const applyLinkSecurity = (root: ParentNode) => {
  root.querySelectorAll("a[href]").forEach((link) => {
    const anchor = link as HTMLAnchorElement;
    const href = anchor.getAttribute("href") || "";

    const isInternalLink =
      href.startsWith("/") ||
      href.startsWith("#") ||
      href.startsWith("?") ||
      (anchor.hostname && anchor.hostname === window.location.hostname);

    if (isInternalLink) {
      anchor.removeAttribute("target");
      anchor.removeAttribute("rel");
    } else {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    }
  });
};

const EAPageMessage: React.FC<IProps> = ({ message }) => {
  const articleRef = useRef<HTMLElement>(null);
  const shadowHostRef = useRef<HTMLDivElement>(null);
  const isIsolated = hasOwnStyles(message);

  useEffect(() => {
    if (isIsolated) {
      const host = shadowHostRef.current;
      if (!host) return;

      const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
      shadow.innerHTML = message;
      applyLinkSecurity(shadow);
    } else {
      if (!articleRef.current) return;
      applyLinkSecurity(articleRef.current);
    }
  }, [message, isIsolated]);

  if (!message || message.length <= 12) return null;

  return (
    <div className="page-message">
      <MdOutlineInfo className="page-message-icon" />
      {isIsolated ? (
        <div ref={shadowHostRef} />
      ) : (
        <article ref={articleRef} dangerouslySetInnerHTML={{ __html: message }} />
      )}
    </div>
  );
};

export default EAPageMessage;
